import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

type AnyObj = Record<string, any>;

export type ReportFilterField<TReq extends AnyObj = AnyObj> = {
  name: keyof TReq & string;

  labelAr: string;
  labelEn: string;

  type: 'text' | 'textarea' | 'number' | 'checkbox' | 'select' | 'tags';

  // UI
  placeholderAr?: string;
  placeholderEn?: string;
  section?: string; // grouping
  col?: number; // bootstrap col-md-?
  hidden?: boolean;

  // for select
  options?: any[];
  isClearable?: boolean;

  // mapping select value <-> request value
  mapIn?: (rawValue: any, ctx: { isAr: boolean }) => any; // raw -> control value
  mapOut?: (controlValue: any, ctx: { isAr: boolean }) => any; // control -> raw

  // select label/value
  getOptionLabel?: (opt: any, ctx: { isAr: boolean }) => string;
  getOptionValue?: (opt: any) => string;

  // optional normalize before apply (e.g., trim)
  normalize?: (rawValue: any) => any;

  // optional default for reset
  defaultValue?: any;
};

export type ReportSearchFiltersModalProps<TReq extends AnyObj = AnyObj> = {
  show: boolean;
  onHide: () => void;

  value: Partial<TReq>;
  fields: ReportFilterField<TReq>[];

  onApply: (patch: Partial<TReq>) => void;

  title?: { ar: string; en: string } | string;
};

const safeStr = (v: any) => (v === null || v === undefined ? '' : String(v));

const defaultGetOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const csvToArray = (csv: any) =>
  safeStr(csv)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

const arrayToCsv = (arr: any[]) =>
  (arr ?? [])
    .map(x => safeStr(x?.value ?? x))
    .map(s => s.trim())
    .filter(Boolean)
    .join(',');

const ReportSearchFiltersModal = <TReq extends AnyObj = AnyObj>(props: ReportSearchFiltersModalProps<TReq>) => {
  const { show, onHide, value, fields, onApply, title } = props;
  const { i18n, t } = useTranslation();
  const isAr = i18n.language !== 'en';

  // Local draft (raw request values)
  const [draft, setDraft] = useState<Partial<TReq>>({});

  useEffect(() => {
    setDraft(value ?? {});
  }, [value, show]);

  const visibleFields = useMemo(() => fields.filter(f => !f.hidden), [fields]);

  const sections = useMemo(() => {
    const map = new Map<string, ReportFilterField<TReq>[]>();
    for (const f of visibleFields) {
      const k = f.section ?? (isAr ? 'عام' : 'General');
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(f);
    }
    return Array.from(map.entries());
  }, [visibleFields, isAr]);

  const getLabel = (f: ReportFilterField<TReq>) => (isAr ? f.labelAr : f.labelEn);
  const getPlaceholder = (f: ReportFilterField<TReq>) => (isAr ? f.placeholderAr : f.placeholderEn);

  const setVal = (name: string, v: any) => setDraft(prev => ({ ...prev, [name]: v }));

  const handleResetLocal = () => {
    const next: AnyObj = { ...(value ?? {}) };
    for (const f of fields) {
      if (f.defaultValue !== undefined) next[f.name] = f.defaultValue;
      else if (f.type === 'checkbox') next[f.name] = false;
      else if (f.type === 'number') next[f.name] = null;
      else next[f.name] = null;
    }
    setDraft(next as Partial<TReq>);
  };

//   const buildPatchAndApply = () => {
//     const patch: AnyObj = {};

//     for (const f of fields) {
//       if (f.hidden) continue;
//       const raw = (draft as AnyObj)[f.name];
//       patch[f.name] = f.normalize ? f.normalize(raw) : raw;
//     }
//     onApply(patch as Partial<TReq>);
//   };

const buildPatchAndApply = () => {
  const patch: AnyObj = {};

  const toCleanValue = (v: any) => {
    // null/undefined => undefined (يعني لا ترسله)
    if (v === null || v === undefined) return undefined;

    // strings: trim ثم لو فاضي => undefined
    if (typeof v === 'string') {
      const s = v.trim();
      return s === '' ? undefined : s;
    }

    // arrays: لو فاضية => undefined
    if (Array.isArray(v)) {
      return v.length === 0 ? undefined : v;
    }

    // numbers/booleans/objects: اتركها كما هي
    // (مهم: 0 و false لا يتم حذفهم)
    return v;
  };

  for (const f of fields) {
    if (f.hidden) continue;

    const raw = (draft as AnyObj)[f.name];
    const normalized = f.normalize ? f.normalize(raw) : raw;

    // ✅ هنا التنظيف
    patch[f.name] = toCleanValue(normalized);
  }

  onApply(patch as Partial<TReq>);
};


  const defaultOptionLabel = (opt: any, ctx: { isAr: boolean }) => {
    if (!opt) return '';
    if (typeof opt.label === 'string') return opt.label;
    const ar = opt.name2 ?? opt.NAME2 ?? opt.labelAr ?? null;
    const en = opt.name ?? opt.NAME ?? opt.labelEn ?? null;
    return ctx.isAr ? safeStr(ar ?? en) : safeStr(en ?? ar);
  };

  const defaultOptionValue = (opt: any) => defaultGetOptId(opt) ?? '';

  const selectPortalStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
  };

  return (
    <Modal show={show} onHide={onHide} size='lg' centered backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>
          {typeof title === 'string'
            ? title
            : title
              ? isAr
                ? title.ar
                : title.en
              : isAr
                ? 'فلاتر البحث'
                : 'Search Filters'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='row g-3'>
          {visibleFields.map(f => {
            const col = f.col ?? 6;
            const rawValue = (draft as AnyObj)[f.name];

            return (
              <div key={f.name} className={`col-12 col-md-${col}`}>
                <label className='form-label mb-1'>{getLabel(f)}</label>

                {f.type === 'text' && (
                  <input
                    className='form-control'
                    value={safeStr(rawValue)}
                    placeholder={getPlaceholder(f)}
                    onChange={e => setVal(f.name, e.target.value)}
                  />
                )}

                {f.type === 'textarea' && (
                  <textarea
                    className='form-control'
                    rows={3}
                    value={safeStr(rawValue)}
                    placeholder={getPlaceholder(f)}
                    onChange={e => setVal(f.name, e.target.value)}
                  />
                )}

                {f.type === 'number' && (
                  <input
                    className='form-control'
                    type='number'
                    value={rawValue === null || rawValue === undefined ? '' : String(rawValue)}
                    placeholder={getPlaceholder(f)}
                    onChange={e => setVal(f.name, e.target.value === '' ? null : Number(e.target.value))}
                  />
                )}

                {f.type === 'checkbox' && (
                  <div className='form-check mt-2'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={!!rawValue}
                      onChange={e => setVal(f.name, e.target.checked)}
                      id={`chk_${f.name}`}
                    />
                    <label className='form-check-label' htmlFor={`chk_${f.name}`}>
                      {getLabel(f)}
                    </label>
                  </div>
                )}

                {f.type === 'select' && (
                  <Select
                    value={
                      f.mapIn
                        ? f.mapIn(rawValue, { isAr })
                        : ((f.options ?? []).find(
                            o => (f.getOptionValue ?? defaultOptionValue)(o) === safeStr(rawValue)
                          ) ?? null)
                    }
                    onChange={opt => {
                      const nextRaw = f.mapOut
                        ? f.mapOut(opt, { isAr })
                        : opt
                          ? (f.getOptionValue ?? defaultOptionValue)(opt)
                          : null;
                      setVal(f.name, nextRaw);
                    }}
                    options={f.options ?? []}
                    placeholder={getPlaceholder(f)}
                    isClearable={f.isClearable ?? true}
                    isSearchable
                    classNamePrefix='react-select'
                    menuPortalTarget={document.body}
                    styles={selectPortalStyles}
                    getOptionLabel={(o: any) => (f.getOptionLabel ?? defaultOptionLabel)(o, { isAr })}
                    getOptionValue={(o: any) => (f.getOptionValue ?? defaultOptionValue)(o)}
                  />
                )}

                {f.type === 'tags' && (
                  <CreatableSelect
                    isMulti
                    value={csvToArray(rawValue).map(v => ({ label: v, value: v }))}
                    onChange={(arr: any) => setVal(f.name, arrayToCsv(arr))}
                    placeholder={getPlaceholder(f) ?? (isAr ? 'اكتب قيمة واضغط Enter' : 'Type value and press Enter')}
                    classNamePrefix='react-select'
                    menuPortalTarget={document.body}
                    styles={selectPortalStyles}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button className='btn btn-outline-secondary' type='button' onClick={handleResetLocal}>
          <i className='fas fa-undo me-2'></i>
          {isAr ? 'إعادة تعيين' : 'Reset'}
        </button>

        <button className='btn btn-outline-secondary' type='button' onClick={onHide}>
          {isAr ? 'إغلاق' : 'Close'}
        </button>

        <button
          className='btn btn-theme'
          type='button'
          onClick={() => {
            buildPatchAndApply();
            onHide();
          }}
        >
          <i className='fas fa-filter me-2'></i>
          {isAr ? 'تطبيق' : 'Apply'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportSearchFiltersModal;
